import { useState, useEffect, FormEvent } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/base/PageHero';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "お問い合わせ - ジュエリーおかやま株式会社",
      "description": "岡山市のジュエリーおかやまへのお問い合わせページ。オーダーメイド、リメイク、ブライダルジュエリーに関するご相談はこちらから。",
      "url": `${siteUrl}/contact`
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6f8rlqau1b90a403rf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          category: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="お問い合わせ"
          subtitle="Contact Us"
          image="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/313aac9c978ef070ef3395c491f5ce8a.jpeg"
        />

        <section className="py-20 lg:py-28 bg-cream-100">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <div className="bg-white rounded-xl shadow-xl p-8 lg:p-12">
              <h2 className="text-2xl md:text-3xl font-serif font-semibold text-brown-600 text-center mb-8">
                お気軽にお問い合わせください
              </h2>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-base font-serif font-semibold text-brown-600 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-brown-300 rounded-lg focus:border-brown-400 focus:outline-none font-serif"
                      placeholder="山田 太郎"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-base font-serif font-semibold text-brown-600 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-brown-300 rounded-lg focus:border-brown-400 focus:outline-none font-serif"
                      placeholder="example@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-base font-serif font-semibold text-brown-600 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-brown-300 rounded-lg focus:border-brown-400 focus:outline-none font-serif"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-base font-serif font-semibold text-brown-600 mb-2">
                      お問い合わせ種別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-brown-300 rounded-lg focus:border-brown-400 focus:outline-none font-serif cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="オーダーメイド">オーダーメイド</option>
                      <option value="リメイク">リメイク</option>
                      <option value="ブライダル">ブライダル</option>
                      <option value="修理">修理</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-base font-serif font-semibold text-brown-600 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-brown-300 rounded-lg focus:border-brown-400 focus:outline-none font-serif resize-none"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                    <p className="text-sm text-brown-400 mt-1 font-serif">
                      {formData.message.length}/500文字
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="text-center pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-12 py-4 bg-brown-400 text-white rounded-lg hover:bg-brown-500 transition-all font-serif text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                      <p className="text-green-700 font-serif text-center">
                        お問い合わせありがとうございます。内容を確認次第、ご連絡させていただきます。
                      </p>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                      <p className="text-red-700 font-serif text-center">
                        送信に失敗しました。お手数ですが、もう一度お試しください。
                      </p>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <article className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mx-auto mb-4">
                  <i className="ri-phone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-serif font-semibold text-brown-600 mb-2">
                  お電話
                </h3>
                <p className="text-base text-brown-500 font-serif">
                  086-012-3456
                </p>
              </article>

              <article className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-serif font-semibold text-brown-600 mb-2">
                  メール
                </h3>
                <p className="text-base text-brown-500 font-serif">
                  info@jewelry-okayama.jp
                </p>
              </article>

              <article className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-serif font-semibold text-brown-600 mb-2">
                  営業時間
                </h3>
                <p className="text-base text-brown-500 font-serif">
                  9:00 〜 18:00<br />
                  定休日：土曜・祝日
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}